import { lookupSpecies } from "./api";
import { hasRealCommonName } from "../../shared/species";
import type { Species } from "./types";

// Real iNat taxon ids are always < this; synthetic ids the merge flow mints
// for GBIF/OBIS-only species are above. Kept in sync with MatchCard and
// worker/factsheet.ts.
const SYNTHETIC_TAXON_MIN = 1_000_000_000;

// A GBIF/OBIS-only candidate often lacks a real common name and/or a photo,
// because those sources don't provide them. Adding it as-is strands the
// lifelist entry at "Stenella attenuata / Stenella attenuata / no photo".
export function speciesNeedsEnrichment(sp: Species): boolean {
  return (
    !hasRealCommonName(sp.common_name, sp.scientific_name) || !sp.photo_url
  );
}

// Fill in a real common name + photo for a thin species by looking it up
// (iNat / Wikipedia), preserving a real iNat taxon_id when the input had one.
// Returns the input unchanged when it's already complete or the lookup fails.
// One place so every "add to lifelist" path enriches the same way — the
// generic counterpart to MatchCard's identify-aware ladder.
export async function enrichForAdd(sp: Species): Promise<Species> {
  if (!speciesNeedsEnrichment(sp)) return sp;
  try {
    const resolved = await lookupSpecies(sp.scientific_name);
    const common_name = hasRealCommonName(sp.common_name, sp.scientific_name)
      ? sp.common_name
      : resolved.common_name;
    return sp.taxon_id < SYNTHETIC_TAXON_MIN
      ? { ...resolved, common_name, taxon_id: sp.taxon_id }
      : { ...resolved, common_name };
  } catch {
    return sp;
  }
}
